import { Box } from '@mui/material';
import Text from 'components/text';
import BaseInput from 'components/inputs/base-input';
import PurpleButton from 'components/purple-button';
import { IPermissions, useUserData } from 'hooks/useUserData';
import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from 'utils/api';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Shown when `validate` answers 410 (expired automated invite, SSM D-35).
 * The resend route always answers 200 and never says whether the address
 * exists, so the confirmation is the same whatever the response.
 */
function ResendForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    api.invitation
      .resend({ data: { email: email.trim() } })
      .catch(() => undefined)
      .then(() => setSent(true));
  };

  if (sent) {
    return (
      <Box fontSize={18}>
        <Text lineHeight={1.3} fontSize={26} paddingBottom={1}>
          {t('Check your inbox')}
        </Text>
        <Text lineHeight={1.3}>
          {t('If an invitation exists for this address, a new link is on its way.')}
        </Text>
      </Box>
    );
  }

  return (
    <Box fontSize={18}>
      <Text lineHeight={1.3} fontSize={26} paddingBottom={1}>
        {t('Your invitation link has expired')}
      </Text>
      <Text lineHeight={1.3} paddingBottom={2}>
        {t('Enter your email address and we will send you a new link.')}
      </Text>
      <form noValidate={false} onSubmit={onSubmit}>
        <BaseInput
          fullWidth
          required
          type="email"
          variant="filled"
          label={t('Email address')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Box marginTop={1}>
          <PurpleButton type="submit">{t('Send new link')}</PurpleButton>
        </Box>
      </form>
    </Box>
  );
}

export default function Invitation() {
  const { t } = useTranslation();
  const history = useHistory();
  const { updateUserData, updatePermissionData } = useUserData();
  const [error, setError] = useState(false);
  const [expired, setExpired] = useState(false);
  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    if (!token) return;

    api.invitation
      .validate({ data: { token } })
      .then((validateResponse) => {
        const { resetPasswordToken } = validateResponse;
        setError(false);
        api.auth
          .getProfile()
          .then((data) => {
            const completeUserData = { ...data, isAuthorized: true };
            updateUserData(completeUserData);

            api.auth.getPermissions().then((permissions) => {
              updatePermissionData(permissions as IPermissions);

              if (resetPasswordToken) {
                history.push(`/new-password?resetPasswordToken=${resetPasswordToken}`);
              } else {
                // land on onboarding until every required artist profile is complete
                const inOnboarding = data.onboarding?.required && !data.onboarding?.completed;
                history.push(inOnboarding ? '/profile' : '/deals');
              }
            });
          })
          .catch((err) => {
            setError(true);
          });
      })
      .catch((err) => {
        if (err?.status === 410) {
          setExpired(true);
        } else {
          setError(true);
        }
      });
  }, [token]);

  return (
    <Box maxWidth="600px" bgcolor="#ddd" margin="auto" marginTop={4} padding={2} borderRadius={2}>
      {expired ? (
        <ResendForm />
      ) : error ? (
        <Box fontSize={18}>
          <Text lineHeight={1.3} fontSize={26} paddingBottom={1}>
            {t('Something went wrong!')}
          </Text>
          <Text lineHeight={1.3}>- {t('Click on your invitation link to try again.')}</Text>
          <Text lineHeight={1.3}>- {t("It's possible that your invitation has expired.")}</Text>
          <Text lineHeight={1.3}>
            -{' '}
            {t(
              "If you're experiencing issues using our service, please reach out to us at contact@snafurecords.com"
            )}
          </Text>
        </Box>
      ) : (
        <Text>{t('Please wait...')}</Text>
      )}
    </Box>
  );
}
